/**
 * Mobile navigation config for couple and supplier views.
 * Supplier bottom bar: Messaging, Couples, Lumi (center), Agreements, Schedules, Menu
 */

import React from 'react';
import { Box } from '@mui/material';
import {
  House,
  ChatCircleText,
  Users,
  Storefront,
  ClockUser,
  ListChecks,
  Calendar,
  List as MenuIcon,
  SignatureIcon,
} from '@phosphor-icons/react';

const createLumiIcon = () => (
  <Box
    component="img"
    src={`${process.env.PUBLIC_URL}/img/lumi.png`}
    alt="Lumi"
    sx={{ width: 30, height: 30 }}
  />
);

/**
 * Supplier bottom nav: Messaging, Couples, Lumi, Agreements, Schedules, Menu
 */
export function getSupplierBottomNav() {
  return [
    { id: 'messages', icon: <ChatCircleText size={24} weight="regular" />, path: '/supplier/messages', label: 'Messages', permissions: ['view_messages'] },
    { id: 'couples', icon: <Users size={24} weight="regular" />, path: '/supplier/crm', label: 'Couples', permissions: ['view_bookings'] },
    { id: 'lumi', icon: createLumiIcon(), path: '/supplier/lumi', label: 'Lumi', isCenter: true },
    { id: 'agreements', icon: <SignatureIcon size={24} weight="regular" />, path: '/supplier/agreements', label: 'Agreements', permissions: ['view_bookings'] },
    { id: 'schedules', icon: <ClockUser size={24} weight="regular" />, path: '/supplier/schedules', label: 'Schedules', permissions: ['manage_availability'] },
    { id: 'menu', icon: <MenuIcon size={24} weight="regular" />, path: null, label: 'Menu', isMenuTrigger: true },
  ];
}

/**
 * Couple bottom nav: Home, Messaging, Suppliers, Lumi, To-Do List, Bookings, Menu
 */
export function getCoupleBottomNav() {
  return [
    {
      id: 'home',
      icon: <House size={24} weight="regular" />,
      path: '/dashboard',
      label: 'Home',
    },
    { id: 'messages', icon: <ChatCircleText size={24} weight="regular" />, path: '/chat', label: 'Messages' },
    {
      id: 'suppliers',
      icon: <Storefront size={24} weight="regular" />,
      path: '/suppliers',
      label: 'Suppliers',
    },
    { id: 'lumi', icon: createLumiIcon(), path: '/lumi', label: 'Lumi', isCenter: true },
    {
      id: 'todos',
      icon: <ListChecks size={24} weight="regular" />,
      path: '/todo-list',
      label: 'To-Do List',
    },
    { id: 'bookings', icon: <Calendar size={24} weight="regular" />, path: '/bookings', label: 'Bookings' },
    { id: 'menu', icon: <MenuIcon size={24} weight="regular" />, path: null, label: 'Menu', isMenuTrigger: true },
  ];
}

/**
 * Supplier menu drawer items (flat list). Filter by permissions in component.
 */
export function getSupplierMenuItems() {
  return [
    { title: 'Apps', path: '/supplier/apps' },
    { title: 'Messages', path: '/supplier/messages', permissions: ['view_messages'] },
    { title: 'Couples', path: '/supplier/crm', permissions: ['view_bookings'] },
    { title: 'Bookings', path: '/supplier/bookings', permissions: ['view_bookings'] },
    { title: 'Invoices', path: '/supplier/invoices', permissions: ['view_financials'] },
    {
      title: 'Agreements',
      permissions: ['view_bookings'],
      children: [
        { title: 'Products', path: '/supplier/products', permissions: ['view_bookings'] },
        { title: 'Your agreements', path: '/supplier/agreements', permissions: ['view_bookings'] },
      ],
    },
    { title: 'Profile', path: '/supplier/profile', permissions: ['view_profile'] },
    {
      title: 'Forms',
      permissions: ['view_bookings'],
      children: [
        { title: 'Form submissions', path: '/supplier/forms/submissions', permissions: ['view_bookings'] },
        {
          title: 'Form builder',
          path: '/supplier/forms/builder',
          activePathPrefixes: ['/supplier/forms/create', '/supplier/forms/edit'],
          permissions: ['manage_bookings']
        },
      ],
    },
    { title: 'Schedules', path: '/supplier/schedules', permissions: ['manage_availability'] },
    { title: 'My Plans & Payments', path: '/supplier/billing', permissions: ['view_financials'] },
    { title: 'People', path: '/supplier/people', permissions: ['manage_staff'] },
  ];
}

/**
 * Couple menu drawer items (flat major product areas)
 */
export function getCoupleMenuItems() {
  return [
    { title: 'Wedding', path: '/wedding-details' },
    { title: 'Suppliers', path: '/suppliers' },
    {
      title: 'Guests',
      children: [
        { title: 'Guest List', path: '/guest-list' },
        { title: 'Wedding Party', path: '/guests/wedding-party' },
        { title: 'Seating Plan', path: '/seating-plan' },
      ],
    },
    { title: 'To-Do List', path: '/todo-list' },
    { title: 'Bookings', path: '/bookings' },
    { title: 'Agreements', path: '/documents' },
    { title: 'Budget', path: '/budget' },
    { title: 'Vows', path: '/vows' },
  ];
}
